import React from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Home,
  User,
  Settings,
  LogOut,
  HelpCircle,
  TrendingUp,
  Award,
  Share2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../lib/supabase";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  onSignOut: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  user,
  onSignOut,
}) => {
  const navigate = useNavigate();

  const menuItems = [
    { icon: Home, label: "Home", path: "/welcome" },
    { icon: TrendingUp, label: "My Calls", path: "/home" },
    { icon: User, label: "Profile", path: "/profile" },
    { icon: Settings, label: "Settings", path: "/settings" },
    { icon: HelpCircle, label: "Help", path: "/help" },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSignOut = () => {
    onSignOut();
    onClose();
    navigate("/login");
  };

  function generateReferralCode(): string {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let code = "";
    for (let i = 0; i < 6; i++) {
      const randomIndex = Math.floor(Math.random() * chars.length);
      code += chars[randomIndex];
    }
    return code;
  }

  const handleShareInvite = async (userId: string) => {
    try {
      const referralCode = generateReferralCode();

      // 1. Log the referral attempt in Supabase
      const { error: supabaseError } = await supabase
        .from("Vitta_Referrals")
        .upsert({
          inviter_id: user.id,
          invitee_id: null,
          referral_code: referralCode,
        });

      if (supabaseError) throw supabaseError;

      // 2. Define your sharing content
      const referralLink = `https://vytta.in/register?ref=${referralCode}`;
      const shareMessage = `Join Vytta and get premium stock recommendations! 📈\n\nUse my referral link: ${referralLink}`;

      // 3. WhatsApp Redirect (Standard WA)
      // Using api.whatsapp.com is the most reliable way to trigger the app on mobile & web
      const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareMessage)}`;

      window.open(whatsappUrl, "_blank");

      // 4. Fallback: Copy to clipboard for better UX
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(referralLink);
        toast.success("Link copied! Redirecting to WhatsApp...");
      }
    } catch (error: any) {
      console.error("Error sharing:", error);
      toast.error("Could not generate referral. Please try again.");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-40"
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed left-0 top-0 bottom-0 w-80 bg-dark-card z-50 shadow-2xl border-r border-dark-border"
          >
            {/* Header with Gradient */}
            <div className="vitta-gradient-bg p-6 relative overflow-hidden">
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition z-10"
              >
                <X size={24} />
              </button>

              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-5 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-5 rounded-full -ml-12 -mb-12"></div>

              {/* User Info */}
              <div className="flex items-center gap-4 mt-8 relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-white bg-opacity-20 flex items-center justify-center backdrop-blur-sm border border-white border-opacity-30 shadow-xl">
                  <span className="text-2xl font-bold">
                    {user?.name?.charAt(0) || "V"}
                  </span>
                </div>
                <div>
                  <h3 className="font-bold text-lg">{user?.name || "User"}</h3>
                  <p className="text-sm opacity-90">{user?.email}</p>
                  <div className="mt-2 px-3 py-1 bg-white bg-opacity-20 rounded-lg inline-flex items-center gap-1 backdrop-blur-sm">
                    <Award size={14} />
                    <p className="text-xs font-semibold">Gold Plan</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="p-4 space-y-2">
              {menuItems.map((item, index) => (
                <motion.button
                  key={item.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleNavigation(item.path)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-dark-surface rounded-xl transition-all group"
                >
                  <item.icon
                    size={20}
                    className="text-gray-400 group-hover:text-accent-primary transition"
                  />
                  <span className="font-medium group-hover:text-white transition">
                    {item.label}
                  </span>
                </motion.button>
              ))}
            </div>
            <button
              onClick={() => handleShareInvite(user?.id)}
              className="flex items-center gap-3 px-4 py-3 hover:bg-dark-surface rounded-lg transition-all w-full text-left"
            >
              <Share2 size={20} className="text-accent-primary" />
              <span>Invite Friends</span>
            </button>

            {/* Sign Out */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-dark-border bg-dark-surface">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-4 py-3 text-accent-error hover:bg-accent-error hover:bg-opacity-10 rounded-xl transition-all"
              >
                <LogOut size={20} />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Sidebar;
